import { useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const TAG = { High: "bg-red-50 text-red-700 ring-red-200", Medium: "bg-amber-50 text-amber-700 ring-amber-200", Low: "bg-slate-100 text-slate-600 ring-slate-200" };
const Tag = ({ v }) => <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${TAG[v] || TAG.Low}`}>{v || "Low"}</span>;

const Findings = ({ items }) => (
  <ol className="space-y-3" data-testid="insights-findings">
    {items.map((f, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">{i + 1}</span>
        <div>
          <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-slate-900">{f.title}</span><Tag v={f.impact} />{f.recurring && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-violet-200" data-testid="finding-recurring-tag">Recurring</span>}</div>
          <p className="mt-0.5 text-sm text-slate-600">{f.detail}</p>
        </div>
      </li>
    ))}
  </ol>
);

const Actions = ({ items }) => (
  <ul className="space-y-2.5" data-testid="insights-actions">
    {items.map((a, i) => (
      <li key={i} className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-start justify-between gap-2"><span className="text-sm font-semibold text-slate-900">{a.action}</span><Tag v={a.priority} /></div>
        <p className="mt-1 text-xs text-slate-500">{a.why}</p>
        <p className="mt-1.5 text-xs font-medium text-slate-700">{a.owner}{a.outlet ? ` · ${a.outlet}` : ""}</p>
      </li>
    ))}
  </ul>
);

export const InsightsPanel = ({ params }) => {
  const [ins, setIns] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify(params);
  const run = (refresh) => {
    setBusy(true);
    api.post("/analytics/insights", { ...JSON.parse(key), refresh }).then((r) => setIns(r.data))
      .catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  useEffect(() => {
    setIns(null);
    run(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <section className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white p-5 sm:p-6" data-testid="insights-panel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="rounded-xl bg-indigo-600 p-2 text-white"><Sparkles className="h-4 w-4" /></span>
          <div>
            <h3 className="text-base font-semibold text-slate-900 sm:text-lg">Patterns & actions</h3>
            <p className="text-xs text-slate-500">{ins ? `${ins.source === "ai" ? "Written by AI from this period's numbers" : "Automatic summary"} · ${fmtDateTime(ins.generated_at)}` : "Reading this period's feedback…"}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => run(true)} disabled={busy} data-testid="insights-refresh-button"><RefreshCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} />Rewrite</Button>
      </div>
      {!ins ? (
        <div className="space-y-3" data-testid="insights-loading"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="font-display text-xl font-semibold leading-snug text-slate-900" data-testid="insights-headline">{ins.headline}</p>
            <p className="mt-2 text-sm text-slate-600">{ins.summary}</p>
          </div>
          {(ins.findings.length > 0 || ins.actions.length > 0) && (
            <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
              <div><h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">What we see</h4><Findings items={ins.findings} /></div>
              <div><h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">What to do</h4><Actions items={ins.actions} /></div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
